// src/components/VisitorCheckoutQR.jsx
import React, { useEffect, useState, useRef } from 'react';
import {
  Paper, Typography, Box, Button, Container, Card, CardContent, Divider, Alert, CircularProgress
} from '@mui/material';
import { QrCode, Download, Refresh } from '@mui/icons-material';
import QRCode from 'qrcode';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { getVisitById, getVisitorById } from '../db/visitorCheckoutQRApi.js';

const VisitorCheckoutQR = ({ visitorData: propVisitorData, visitData: propVisitData }) => {
  const { user } = useAuth();
  const location = useLocation();
  const visitFromState = location.state?.visit;

  const [visitData, setVisitData] = useState(visitFromState || propVisitData || null);
  const [visitorData, setVisitorData] = useState(
    visitFromState
      ? {
        visitorUserID: visitFromState.visitorUserID,
        visitorName: visitFromState.visitorName,
        visitorEmail: visitFromState.visitorEmail
      }
      : propVisitorData || null
  );
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const canvasRef = useRef(null);

  // If no visit data, try to fetch based on params from URL
  useEffect(() => {
    const fetchData = async () => {
      if (!visitData && location.state?.visitId) {
        setLoading(true);
        try {
          const visit = await getVisitById(location.state.visitId);
          setVisitData(visit);
          if (visit?.visitorUserID) {
            const visitor = await getVisitorById(visit.visitorUserID);
            setVisitorData(visitor);
          }
        } catch (err) {
          setError('Failed to fetch visit data');
        } finally {
          setLoading(false);
        }
      } else if (!visitData) {
        setError('No visit selected. Please select a visit from the previous page.');
      }
    };

    fetchData();
  }, [visitData, location.state]);

  const generateCheckoutURL = () => {
    if (!visitorData || !visitData) return '';
    const params = new URLSearchParams({
      visitId: visitData.id,
      visitorUserID: visitorData.visitorUserID,
      visitorName: visitorData.visitorName,
      visitorEmail: visitorData.visitorEmail,
      hostUserID: visitData.hostUserID,
      visitReason: visitData.visitReason || '',
      checkInTime: visitData.visitorTime || ''
    });
    return `${window.location.origin}/visitor-checkout?${params.toString()}`;
  };

  const generateQRCode = async () => {
    setIsGenerating(true);
    try {
      const url = generateCheckoutURL();
      if (!url) return;
      const qrDataUrl = await QRCode.toDataURL(url, {
        width: 500,
        margin: 8,
        color: { dark: '#000000', light: '#ffffff' }
      });
      setQrCodeUrl(qrDataUrl);
      if (canvasRef.current) {
        await QRCode.toCanvas(canvasRef.current, url, { width: 500, margin: 8 });
      }
    } catch (err) {
      console.error('QR generation failed:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadQRCode = () => {
    if (qrCodeUrl) {
      const link = document.createElement('a');
      link.href = qrCodeUrl;
      link.download = `visitor-checkout-qr.png`;
      link.click();
    }
  };

  useEffect(() => {
    if (visitorData && visitData) generateQRCode();
  }, [visitorData, visitData]);

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 2 }}>
      <Paper
        sx={{
          borderRadius: "20px",
          overflow: "hidden",
          border: "1px solid #f0d060",
          background: "#ffffff"
        }}
      >

        {/* HEADER */}
        <Box
          sx={{
            background: "#fffbea",
            borderBottom: "1px solid #f0d060",
            p: 3,
            textAlign: "center"
          }}
        >
          <Box
            sx={{
              width: 70,
              height: 70,
              mx: "auto",
              mb: 2,
              borderRadius: "50%",
              background: "#fde047",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <QrCode
              sx={{
                color: "#1c1400",
                fontSize: 40
              }}
            />
          </Box>

          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "1.4rem",
              color: "#1c1400",
              fontFamily: "'Unbounded', sans-serif"
            }}
          >
            Visitor Checkout Pass
          </Typography>

          <Typography sx={{ color: "#8a7a40", mt: 1 }}>
            Scan this QR code to complete checkout
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* VISITOR DETAILS */}
          {visitorData && visitData && (
            <Paper
              sx={{
                p: 2,
                mb: 3,
                background: "#fffbea",
                border: "1px solid #f0d060",
                borderRadius: "12px"
              }}
            >
              <Typography sx={{ fontWeight: 700, mb: 1 }}>
                Visitor Details
              </Typography>

              <Typography variant="body2">
                <strong>Name:</strong> {visitorData.visitorName}
              </Typography>

              <Typography variant="body2">
                <strong>Email:</strong> {visitorData.visitorEmail}
              </Typography>

              <Typography variant="body2">
                <strong>Purpose:</strong> {visitData.visitReason || "N/A"}
              </Typography>
            </Paper>
          )}

          {/* QR SECTION */}
          <Box
            sx={{
              textAlign: "center",
              p: 3,
              borderRadius: "16px",
              background: "#fffbea",
              border: "2px dashed #f0d060"
            }}
          >
            {qrCodeUrl ? (
              <Box>
                <img
                  src={qrCodeUrl}
                  alt="Checkout QR"
                  style={{
                    width: "100%",
                    maxWidth: 320,
                    borderRadius: 12,
                    background: "#fff",
                    padding: 16,
                    border: "1px solid #f0d060"
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  height: 320,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center"
                }}
              >
                <Typography>
                  {isGenerating
                    ? "Generating QR Code..."
                    : "QR Code will appear here"}
                </Typography>
              </Box>
            )}
          </Box>

          {/* ACTION BUTTONS */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              mt: 3,
              flexWrap: "wrap"
            }}
          >
            <Button
              startIcon={<Refresh />}
              onClick={generateQRCode}
              disabled={isGenerating}
              sx={{
                borderRadius: "10px",
                border: "1px solid #f0d060",
                color: "#1c1400",
                px: 3
              }}
            >
              Regenerate
            </Button>

            <Button
              startIcon={<Download />}
              onClick={downloadQRCode}
              disabled={!qrCodeUrl}
              sx={{
                background: "#1c1400",
                color: "#fde047",
                borderRadius: "10px",
                px: 3,

                "&:hover": {
                  background: "#f59e0b",
                  color: "#1c1400"
                }
              }}
            >
              Download QR
            </Button>
          </Box>

          {/* INFO */}
          <Alert
            severity="info"
            sx={{
              mt: 3,
              borderRadius: "10px"
            }}
          >
            This QR contains visitor checkout information and redirects directly to the checkout page.
          </Alert>

        </Box>

        <canvas
          ref={canvasRef}
          style={{ display: "none" }}
        />
      </Paper>
    </Container>
  );
};

export default VisitorCheckoutQR;
